/**
 * Create proposal structures from per-structure timber lists.
 *
 * Normally structures are born when one Cadwork timber list (with a Building /
 * Structure column) is imported in the Proposal Builder. When the designer instead
 * uploads one timber-list track per structure on Files, nothing creates the
 * proposal `nodeSets`, so the structure breakdown never unlocks. This module lets
 * staff create (or fill) proposal structures straight from those tracks and tags
 * each track to the structure it belongs to, so the usual split / per-structure
 * deliverables flow takes over from there.
 */

import { doc, getDoc, updateDoc, Timestamp, type DocumentData } from 'firebase/firestore';
import { db } from './firebase';
import { createDefaultNodeSet, type ProjectFile, type ProjectFileStream, type ProposalBuilderData, type ProposalNodeSet } from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { cadworkParsedItemsToNodeSets, parseTimberCsvForRfqHub } from './proposalCSVParser';
import { headVersionForStream, versionsForStream } from './projectFileStreamVersions';
import {
  moveProjectFileVersionsToStream,
  recordStreamStructureSetup,
  updateProjectFileStreamStructures,
} from './projectFileService';

/** One row in the "Set up structures" wizard. */
export interface StructureFromTimberListRow {
  key: string;
  /** Structure name (used for new structures; ignored when `existingStructureId` is set). */
  name: string;
  /** `null` creates a new proposal structure; otherwise fill / tag this existing one. */
  existingStructureId: string | null;
  /** `timber_list_csv` track tied to this structure on Files. Optional. */
  timberListStreamId: string | null;
  /** Which version on that track supplies the CSV; defaults to the track head when omitted. */
  timberListFileId: string | null;
  /**
   * The CSV has a Building / Room column with several groups and this row should become
   * one structure per group (named from the CSV, exactly like a Proposal Builder import).
   * The track is then tagged to all of them — a structure *group* on Files.
   */
  splitByGroup?: boolean;
  /** Group names the wizard detected in the chosen CSV (preview; the service re-reads the file). */
  groupNames?: string[];
}

/** A row does something when it names a structure, picks an existing one, or splits a CSV by group. */
export function isLiveStructureRow(row: StructureFromTimberListRow): boolean {
  return !!row.existingStructureId || !!row.name.trim() || (!!row.splitByGroup && (row.groupNames?.length ?? 0) > 1);
}

/** Names a row will produce: the detected groups when splitting, else its single name. */
export function structureNamesForRow(row: StructureFromTimberListRow): string[] {
  if (!row.existingStructureId && row.splitByGroup && (row.groupNames?.length ?? 0) > 1) return row.groupNames!;
  return row.name.trim() ? [row.name.trim()] : [];
}

export interface StructureFromTimberListResult {
  created: StructureRef[];
  /** Existing structures that received a timber list or a track tag. */
  updated: StructureRef[];
  /** Wizard row key → structures it produced (one, or several when split by group). */
  structuresByRow: Record<string, StructureRef[]>;
  /** Track id → structures it was tagged to (includes tracks created by moving a version out). */
  taggedStreams: { streamId: string; structures: StructureRef[] }[];
  /** Versions moved onto their own structure track because several structures shared one track. */
  movedVersions: { fileId: string; fromStreamId: string; toStreamId: string; structures: StructureRef[] }[];
  failures: string[];
}

type StreamLite = Pick<ProjectFileStream, 'id' | 'title' | 'streamKind' | 'lastVersionIndex' | 'headFileId' | 'structureIds' | 'forkedFromStreamId' | 'wholeProjectRoot'>;

const NAME_NOISE = /\b(timber\s*lists?|timberlists?|timber|lists?|cadwork|csv|export|material\s*lists?|bom|cut\s*lists?)\b/gi;
/** Revision markers in Cadwork exports: `LOD200`, `LOD200.4`, `LOD 100`. */
const LOD_TOKEN = /\blod\s*\d+(?:\.\d+)*\b/gi;

/**
 * "Barn timber list.csv" → "Barn"; "Timber List - Pavilion" → "Pavilion";
 * "EQUIPMENT BARN_LOD200.4.csv" → "Equipment Barn". Falls back to the trimmed title,
 * then to `fallback`.
 */
export function suggestStructureNameFromTrack(title: string | undefined | null, fallback = 'Structure'): string {
  let s = (title ?? '').replace(/\.[a-z0-9]{2,5}$/i, '');
  s = s.replace(/[_]+/g, ' ');
  s = s.replace(NAME_NOISE, ' ');
  s = s.replace(LOD_TOKEN, ' ');
  s = s.replace(/[([]\s*[)\]]/g, ' ');
  s = s.replace(/\s*[-–—:|·]+\s*/g, ' ');
  s = s.replace(/\s{2,}/g, ' ').trim();
  s = s.replace(/^[\s\-–—:|·(]+|[\s\-–—:|·)]+$/g, '').trim();
  if (!s) {
    const raw = (title ?? '').trim();
    return raw || fallback;
  }
  // ALL-CAPS export names read better as a structure name in title case.
  if (/[A-Z]/.test(s) && s === s.toUpperCase()) {
    s = s.replace(/\b[A-Z][A-Z0-9']*\b/g, w => w.charAt(0) + w.slice(1).toLowerCase());
  }
  return s;
}

/**
 * Timber-list tracks that could seed a structure: `timber_list_csv`, at least one
 * version, not tagged to a structure yet, and not a split fork / root.
 */
export function timberListTracksForStructureSetup<T extends StreamLite>(streams: ReadonlyArray<T>): T[] {
  return streams.filter(
    s =>
      !!s.id &&
      s.streamKind === 'timber_list_csv' &&
      ((s.lastVersionIndex ?? 0) > 0 || !!s.headFileId) &&
      (s.structureIds ?? []).filter(Boolean).length === 0 &&
      !s.forkedFromStreamId &&
      !s.wholeProjectRoot,
  );
}

/**
 * Default wizard rows. Whole-project uploads often stack several structures on one track
 * (EQUIPMENT BARN.csv, FEED BARN.csv, …), so each *distinctly named* version seeds its own
 * row; revisions of the same list (LOD200.1, LOD200.2 → same suggested name) collapse into
 * one row on the newest of them. A track with more than {@link MAX_SEEDED_ROWS_PER_TRACK}
 * distinct names is a long revision history, not a stack of structures — it seeds one row
 * on its newest version. Across tracks, a repeated name gets a numeric suffix.
 */
export const MAX_SEEDED_ROWS_PER_TRACK = 4;

export function seedStructureRowsFromTracks(
  tracks: ReadonlyArray<StreamLite>,
  files: ReadonlyArray<ProjectFile> = [],
): StructureFromTimberListRow[] {
  const seenAcrossTracks = new Map<string, number>();
  const rows: StructureFromTimberListRow[] = [];
  for (const t of tracks.filter(tr => !!tr.id)) {
    // Newest first, so the first version seen for a name wins.
    const versions = files.length ? versionsForStream(t as ProjectFileStream, files as ProjectFile[]) : [];
    const fallback = suggestStructureNameFromTrack(t.title, `Structure ${rows.length + 1}`);
    const seedsForTrack: { name: string; fileId: string | null; key: string }[] = [];
    const namesOnTrack = new Set<string>();
    if (versions.length === 0) {
      seedsForTrack.push({ name: fallback, fileId: t.headFileId ?? null, key: `row-${t.id}-head` });
    } else {
      for (const v of versions) {
        const name = (v.name ? suggestStructureNameFromTrack(v.name) : '') || fallback;
        const k = name.toLowerCase();
        if (namesOnTrack.has(k)) continue;
        namesOnTrack.add(k);
        seedsForTrack.push({ name, fileId: v.id ?? null, key: `row-${t.id}-${v.id}` });
      }
      if (seedsForTrack.length > MAX_SEEDED_ROWS_PER_TRACK) {
        const newest = versions[0];
        seedsForTrack.splice(0, seedsForTrack.length, {
          name: fallback,
          fileId: newest.id ?? null,
          key: `row-${t.id}-${newest.id}`,
        });
      }
    }
    for (const seed of seedsForTrack) {
      const k = seed.name.toLowerCase();
      const n = (seenAcrossTracks.get(k) ?? 0) + 1;
      seenAcrossTracks.set(k, n);
      rows.push({
        key: seed.key,
        name: n > 1 ? `${seed.name} ${n}` : seed.name,
        existingStructureId: null,
        timberListStreamId: t.id!,
        timberListFileId: seed.fileId,
      });
    }
  }
  return rows;
}

export interface TimberListTrackPlan {
  streamId: string;
  /** Structures the original track keeps (its version stays put). */
  keep: { structures: StructureRef[]; fileId: string | null };
  /** Versions that leave for their own new track, one per wizard row. */
  moveOut: { structures: StructureRef[]; fileId: string; newTitle: string }[];
}

/**
 * How each timber-list track is reshaped after the wizard. One row per track → just tag
 * it (to one structure, or to the whole group when the CSV was split by Building). Several
 * rows from different versions on one whole-project track → the track keeps one version
 * (the head's row when it is among them) and every other version moves onto its own new
 * track named from the file, tagged to its structures.
 */
export function planTimberListTrackChanges(
  live: ReadonlyArray<StructureFromTimberListRow>,
  structuresForRow: ReadonlyMap<string, StructureRef[]>,
  streams: ReadonlyArray<Pick<ProjectFileStream, 'id' | 'headFileId' | 'title'>>,
  files: ReadonlyArray<Pick<ProjectFile, 'id' | 'name'>>,
): TimberListTrackPlan[] {
  const byStream = new Map<string, { structures: StructureRef[]; fileId: string | null }[]>();
  for (const row of live) {
    if (!row.timberListStreamId) continue;
    const structures = structuresForRow.get(row.key);
    if (!structures?.length) continue;
    const list = byStream.get(row.timberListStreamId) ?? [];
    list.push({ structures, fileId: row.timberListFileId });
    byStream.set(row.timberListStreamId, list);
  }
  const plans: TimberListTrackPlan[] = [];
  byStream.forEach((entries, streamId) => {
    const stream = streams.find(s => s.id === streamId);
    const headId = stream?.headFileId ?? null;
    let keepIdx = entries.findIndex(e => e.fileId && e.fileId === headId);
    if (keepIdx < 0) keepIdx = entries.findIndex(e => !e.fileId);
    if (keepIdx < 0) keepIdx = entries.length - 1;
    const keep = entries[keepIdx];
    const moveOut = entries
      .filter((e, i) => i !== keepIdx && !!e.fileId)
      .map(e => {
        const file = files.find(f => f.id === e.fileId);
        const stem = (file?.name ?? '').replace(/\.[a-z0-9]{2,5}$/i, '').trim();
        const names = e.structures.map(s => s.name).join(' + ');
        return { structures: e.structures, fileId: e.fileId!, newTitle: stem || `${names} — ${stream?.title ?? 'timber list'}` };
      });
    plans.push({ streamId, keep, moveOut });
  });
  return plans;
}

/** Human title from an uploaded CSV filename (e.g. `FEED BARN_LOD200.csv` → `Feed Barn Lod200`). */
export function timberListVersionFileTitle(fileName: string | undefined | null): string {
  const raw = (fileName ?? '').replace(/\.[a-z0-9]{2,5}$/i, '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (!raw) return 'Timber list';
  return raw.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

/** Label for a timber-list version in the setup wizard (file title + revision, like the timeline). */
export function timberListVersionOptionLabel(
  file: Pick<ProjectFile, 'versionLabel' | 'versionIndex' | 'isHeadVersion' | 'name' | 'trueDimensions'>,
): string {
  const title = timberListVersionFileTitle(file.name);
  const idx = file.versionIndex ?? '?';
  const rev = (file.versionLabel || '').trim();
  const revPart = rev ? `${rev} #${idx}` : `#${idx}`;
  const head = file.isHeadVersion ? ' · latest' : '';
  const exact = file.trueDimensions ? ' · True dimensions' : '';
  return `${title} · ${revPart}${head}${exact}`;
}

/** CSV source for one wizard row: explicit version, else the track head. */
export function resolveTimberListFileForRow(
  row: Pick<StructureFromTimberListRow, 'timberListStreamId' | 'timberListFileId'>,
  streams: ReadonlyArray<Pick<ProjectFileStream, 'id' | 'headFileId' | 'forkedFromStreamId'>>,
  files: ReadonlyArray<ProjectFile>,
): ProjectFile | undefined {
  if (!row.timberListStreamId) return undefined;
  const stream = streams.find(s => s.id === row.timberListStreamId);
  if (!stream) return undefined;
  if (row.timberListFileId) {
    const chosen = files.find(f => f.id === row.timberListFileId && f.streamId === row.timberListStreamId);
    if (chosen) return chosen;
  }
  return headVersionForStream(stream, files as ProjectFile[]);
}

export interface BuiltStructureMaterials {
  timberList: ProposalNodeSet['materials']['timberList'];
  totalBoardFeet: number;
  itemCount: number;
}

/**
 * Parse one timber-list CSV into the materials for a single structure. Every row of
 * the CSV belongs to this structure, so any Building / Structure column is ignored
 * (`singleStructureName`). Returns `null` when the CSV has no usable timber rows.
 */
export function buildStructureMaterialsFromCsv(
  csvText: string,
  structureName: string,
  sourceProjectFileId?: string,
  options?: { trueDimensions?: boolean },
): BuiltStructureMaterials | null {
  const parsed = parseTimberCsvForRfqHub(csvText, options);
  if (!parsed.items.length) return null;
  const [ns] = cadworkParsedItemsToNodeSets(parsed.items, {
    singleStructureName: structureName,
    sourceProjectFileId,
    trueDimensions: options?.trueDimensions,
  });
  if (!ns) return null;
  return {
    timberList: ns.materials.timberList,
    totalBoardFeet: ns.materials.totalBoardFeet,
    itemCount: ns.materials.timberList.length,
  };
}

/**
 * Group names in a CSV that could each become a structure (Building / Room column with
 * two or more distinct values, as the Proposal Builder import would split it). Empty when
 * the list is a single structure.
 */
export function detectTimberListGroups(csvText: string, options?: { trueDimensions?: boolean }): string[] {
  const parsed = parseTimberCsvForRfqHub(csvText, options);
  if (!parsed.items.length) return [];
  const names = Array.from(new Set(parsed.items.map(i => (i.group || '').trim()).filter(Boolean)));
  return names.length > 1 ? names : [];
}

/** One structure per CSV group (Building / Room), each with its own timber lines. */
export function buildGroupedStructureMaterialsFromCsv(
  csvText: string,
  sourceProjectFileId?: string,
  options?: { trueDimensions?: boolean },
): { name: string; materials: BuiltStructureMaterials }[] {
  const parsed = parseTimberCsvForRfqHub(csvText, options);
  if (!parsed.items.length) return [];
  return cadworkParsedItemsToNodeSets(parsed.items, {
    sourceProjectFileId,
    trueDimensions: options?.trueDimensions,
  }).map(ns => ({
    name: ns.name,
    materials: {
      timberList: ns.materials.timberList,
      totalBoardFeet: ns.materials.totalBoardFeet,
      itemCount: ns.materials.timberList.length,
    },
  }));
}

/** New nodeSet for `name`, optionally pre-filled with timber materials. */
export function buildStructureNodeSet(
  name: string,
  materials: BuiltStructureMaterials | null,
  index: number,
  previous?: ProposalNodeSet,
): ProposalNodeSet {
  const ns = createDefaultNodeSet(name.trim());
  ns.id = `nodeset-tl-${Date.now()}-${index}-${Math.floor(Math.random() * 1000)}`;
  let y = 0;
  if (previous) y = previous.position.y + (previous.dimensions?.height || 240) + 50;
  ns.position = { x: 50, y };
  if (materials) applyMaterials(ns, materials);
  return ns;
}

function applyMaterials(ns: ProposalNodeSet, materials: BuiltStructureMaterials): void {
  const bf = Math.round(materials.totalBoardFeet * 100) / 100;
  ns.materials = { ...ns.materials, timberList: materials.timberList, totalBoardFeet: bf };
  ns.production = { ...ns.production, boardFeet: bf };
}

/** An existing structure only takes a timber list when it has none yet (never overwrite estimator work). */
export function existingStructureAcceptsTimberList(ns: Pick<ProposalNodeSet, 'materials'>): boolean {
  return (ns.materials?.timberList?.length ?? 0) === 0;
}

export interface CreateStructuresFromTimberListsInput {
  dealId: string;
  projectId: string;
  rows: ReadonlyArray<StructureFromTimberListRow>;
  streams: ReadonlyArray<ProjectFileStream>;
  files: ReadonlyArray<ProjectFile>;
  /** Who is doing this (recorded on moved versions). */
  actor: { uid: string; name: string };
  /** Injectable for tests; defaults to `fetch(url).text()`. */
  fetchText?: (url: string) => Promise<string>;
}

const defaultFetchText = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not download the timber list (${res.status})`);
  return res.text();
};

/**
 * Append / fill proposal structures from the wizard rows, then tag each tied
 * timber-list track to its structure. One write to `proposalBuilders/{dealId}`,
 * then one stream update per tied track. Partial failures are reported, not thrown.
 */
export async function createProposalStructuresFromTimberLists(
  input: CreateStructuresFromTimberListsInput,
): Promise<StructureFromTimberListResult> {
  const { dealId, projectId, rows, streams, files, actor } = input;
  const fetchText = input.fetchText ?? defaultFetchText;
  const result: StructureFromTimberListResult = {
    created: [],
    updated: [],
    structuresByRow: {},
    taggedStreams: [],
    movedVersions: [],
    failures: [],
  };

  const live = rows.filter(isLiveStructureRow);
  if (live.length === 0) return result;

  const ref = doc(db, 'proposalBuilders', dealId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('No proposal found for this deal. Create a proposal first.');
  const data = snap.data() as ProposalBuilderData;
  const nodeSets: ProposalNodeSet[] = [...(data.nodeSets ?? [])];

  // Resolve materials per row (chosen timber-list version), tolerating download/parse failures.
  // A row split by group yields one entry per CSV group; everything else yields one.
  const headByRow = new Map<string, ProjectFile | undefined>();
  const materialsByRow = new Map<string, { name: string; materials: BuiltStructureMaterials | null }[]>();
  for (const row of live) {
    if (!row.timberListStreamId) continue;
    const stream = streams.find(s => s.id === row.timberListStreamId);
    const head = resolveTimberListFileForRow(row, streams, files as ProjectFile[]);
    headByRow.set(row.key, head);
    const splitByGroup = !row.existingStructureId && !!row.splitByGroup;
    const structureName = row.existingStructureId
      ? nodeSets.find(n => n.id === row.existingStructureId)?.name || row.name
      : row.name;
    if (!head?.url) {
      materialsByRow.set(row.key, (splitByGroup ? row.groupNames ?? [] : [structureName]).map(name => ({ name, materials: null })));
      continue;
    }
    try {
      const text = await fetchText(head.url);
      const parseOptions = { trueDimensions: head.trueDimensions === true };
      if (splitByGroup) {
        const grouped = buildGroupedStructureMaterialsFromCsv(text, head.id, parseOptions);
        if (grouped.length > 1) {
          materialsByRow.set(row.key, grouped);
          continue;
        }
        // The CSV turned out to be a single structure after all — fall back to one named structure.
        const fallbackName = structureName.trim() || row.groupNames?.[0] || grouped[0]?.name || 'Structure';
        materialsByRow.set(row.key, [{ name: fallbackName, materials: buildStructureMaterialsFromCsv(text, fallbackName, head.id, parseOptions) }]);
        continue;
      }
      materialsByRow.set(row.key, [{ name: structureName, materials: buildStructureMaterialsFromCsv(text, structureName.trim(), head.id, parseOptions) }]);
    } catch (err) {
      materialsByRow.set(row.key, (splitByGroup ? row.groupNames ?? [] : [structureName]).map(name => ({ name, materials: null })));
      result.failures.push(
        `${stream?.title || 'Timber list'}: ${err instanceof Error ? err.message : 'could not read the CSV'} — structure created without timber lines.`,
      );
    }
  }

  // Build the new nodeSets list.
  const structuresForRow = new Map<string, StructureRef[]>();
  let appliedFileId: string | null = null;
  let newIndex = 0;
  live.forEach(row => {
    const entries = materialsByRow.get(row.key) ?? [{ name: row.name, materials: null }];
    if (row.existingStructureId) {
      const idx = nodeSets.findIndex(n => n.id === row.existingStructureId);
      if (idx < 0) {
        result.failures.push(`${row.name || 'Structure'}: the chosen proposal structure no longer exists.`);
        return;
      }
      const existing = nodeSets[idx];
      const materials = entries[0]?.materials ?? null;
      if (materials && existingStructureAcceptsTimberList(existing)) {
        const next = { ...existing, materials: { ...existing.materials }, production: { ...existing.production } };
        applyMaterials(next, materials);
        nodeSets[idx] = next;
        const head = headByRow.get(row.key);
        if (head?.id) appliedFileId = head.id;
      }
      const refOut = { id: existing.id, name: existing.name };
      structuresForRow.set(row.key, [refOut]);
      result.updated.push(refOut);
      return;
    }
    const refs: StructureRef[] = [];
    for (const entry of entries) {
      const name = entry.name.trim() || row.name.trim() || 'Structure';
      const ns = buildStructureNodeSet(name, entry.materials, newIndex++, nodeSets[nodeSets.length - 1]);
      nodeSets.push(ns);
      const refOut = { id: ns.id, name: ns.name };
      refs.push(refOut);
      result.created.push(refOut);
      if (entry.materials) {
        const head = headByRow.get(row.key);
        if (head?.id) appliedFileId = head.id;
      }
    }
    structuresForRow.set(row.key, refs);
  });
  structuresForRow.forEach((refs, key) => {
    result.structuresByRow[key] = refs;
  });

  const update: DocumentData = { nodeSets, updatedAt: Timestamp.now() };
  const promptFileId = data.latestTimberListPrompt?.fileId;
  if (
    promptFileId &&
    live.some(r => headByRow.get(r.key)?.id === promptFileId && materialsByRow.get(r.key)?.some(e => e.materials))
  ) {
    update.timberListAppliedToProposalFileId = promptFileId;
  } else if (appliedFileId && !data.timberListAppliedToProposalFileId) {
    update.timberListAppliedToProposalFileId = appliedFileId;
  }
  await updateDoc(ref, update);

  // Reshape the tracks: extra versions move to their own structure track, then the
  // original is tagged to the one structure it keeps. Each track is stamped with a
  // `structureSetup` record so the Deliverables history card can tell the story.
  const createdIds = new Set(result.created.map(s => s.id));
  const fileNameOf = (fileId: string | null | undefined) =>
    fileId ? files.find(f => f.id === fileId)?.name : undefined;
  const stamp = async (streamId: string, structures: StructureRef[], extra: { fromStreamId?: string; fromStreamTitle?: string; fileName?: string }) => {
    try {
      await recordStreamStructureSetup(projectId, streamId, {
        byUid: actor.uid,
        byName: actor.name || undefined,
        createdStructure: structures.some(s => createdIds.has(s.id)),
        ...extra,
      });
    } catch (err) {
      console.warn('[proposalStructuresFromTimberLists] could not stamp structureSetup', err);
    }
  };
  for (const plan of planTimberListTrackChanges(live, structuresForRow, streams, files)) {
    const title = streams.find(s => s.id === plan.streamId)?.title || 'Timber list';
    for (const mv of plan.moveOut) {
      try {
        const res = await moveProjectFileVersionsToStream(
          projectId,
          [mv.fileId],
          { newTrack: { title: mv.newTitle, structures: mv.structures } },
          actor,
        );
        result.movedVersions.push({
          fileId: mv.fileId,
          fromStreamId: plan.streamId,
          toStreamId: res.targetStreamId,
          structures: mv.structures,
        });
        result.taggedStreams.push({ streamId: res.targetStreamId, structures: mv.structures });
        await stamp(res.targetStreamId, mv.structures, {
          fromStreamId: plan.streamId,
          fromStreamTitle: title,
          fileName: fileNameOf(mv.fileId),
        });
      } catch (err) {
        result.failures.push(
          `${mv.newTitle}: ${err instanceof Error ? err.message : 'could not move the version to its own track'}`,
        );
      }
    }
    try {
      await updateProjectFileStreamStructures(projectId, plan.streamId, plan.keep.structures);
      result.taggedStreams.push({ streamId: plan.streamId, structures: plan.keep.structures });
      await stamp(plan.streamId, plan.keep.structures, { fileName: fileNameOf(plan.keep.fileId) });
    } catch (err) {
      result.failures.push(`${title}: ${err instanceof Error ? err.message : 'could not tag the track'}`);
    }
  }

  return result;
}
